import type { DeliveryFailedAdvice } from '@/features/SalesforceIngest/lib/deliveryFailedAdvice'
import type { AdvisoryCode } from '@/features/SalesforceIngest/lib/reasonCodes'
import { Alert, Code, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

type Props = {
  code: AdvisoryCode
  /**
   * For `delivery-failed` only, once the payload parses: which of the four
   * situations this actually is, derived from the paste. Absent before the
   * paste parses, when the generic advice stands in.
   */
  deliveryAdvice?: DeliveryFailedAdvice
}

/**
 * What the form says about a code it can only resolve with a caution.
 *
 * It sits above a form that stays usable — every code the select offers is
 * resolvable here on at least one branch, so the panel warns and never
 * replaces. The select stays above and is clearable.
 *
 * The advice is a summary, and deliberately carries no runbook link of its
 * own: the select directly above already has one, and a second copy a few
 * lines below it is noise.
 *
 * It also says nothing about the notification being out of date, even where
 * that is currently true. Encoding one system's temporary state in another
 * system's copy buys a sentence now and a permanent obligation to remember to
 * delete it, which is how the two drift.
 */
export function ReasonCodeGuidance({ code, deliveryAdvice }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  // The paste answers both questions that decide what a `delivery-failed`
  // case needs, so once it parses the panel says the one thing rather than
  // listing four and leaving the operator to match.
  const adviceKey =
    code === 'delivery-failed' && deliveryAdvice
      ? `salesforceIngest.form.guidance.delivery-failed.${deliveryAdvice}`
      : `salesforceIngest.form.guidance.${code}.advice`

  return (
    /* `role` is explicit, following `SalesforceLinkFormDialog`: Chakra v3's
       `Alert.Root` renders a plain div with no implicit role, and this panel
       appears in response to the operator selecting a code, so without it the
       caution on their case is announced to nobody. */
    <Alert.Root status="warning" role="alert" alignItems="start">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>
          <Code colorPalette="orange" me={1.5}>
            {code}
          </Code>
          {String(t('salesforceIngest.form.guidance.mayNotBelongHere'))}
        </Alert.Title>
        <Alert.Description>
          <VStack align="start" gap={2}>
            <Text>{String(t(adviceKey))}</Text>

            {/* This mapping duplicates backend knowledge across two repos, so
                a seventh internal reason would make it quietly wrong rather
                than break anything. Name the authority — for this code only,
                where the line is generated per internal reason. */}
            {code === 'delivery-failed' ? (
              <Text fontSize="sm" color="text.text-subtle">
                {String(
                  t('salesforceIngest.form.guidance.nextStepIsAuthoritative'),
                )}
              </Text>
            ) : null}
          </VStack>
        </Alert.Description>
      </Alert.Content>
    </Alert.Root>
  )
}
